import React, { useState, useEffect } from 'react';
import { Plus, Settings, Play, Trash2, Edit, Calendar, User, Repeat, AlertCircle, ChevronDown } from 'lucide-react';
import Modal from '../modules/Modal';
import { SearchSelect, Checkbox } from '../modules/FieldComponents';
import { useNavVariant } from '../App';

const AutoTaskModal = ({
    editingTask,
    loading,
    formData,
    setFormData,
    autoTasks,
    roleOptions,
    caseTypeOptions,
    saveAutoTask,
    onClose
}) => (
    <Modal
        title={editingTask ? 'Edit Auto Task' : 'Create Auto Task'}
        instructions="Configure automatic task creation rules"
        onClose={onClose}
        footer={
            <div className='modal-footer-actions'>
                <button className="action" onClick={saveAutoTask} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Auto Task'}
                </button>
                <button className="action alt" onClick={onClose}>
                    Cancel
                </button>
            </div>
        }
    >
        <div className='modal-content-wrapper'>
            <div className="form-group full">
                <label className="subtext">Auto Task Name</label>
                <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Send Initial Documents"
                />
            </div>

            <div className="form-group filter-group" style={{ width: '100% !important' }}>
                <label className="subtext">Trigger Type</label>
                <div className='select-wrapper'>
                    <select
                        className='filter-select'
                        value={formData.trigger_type}
                        onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
                    >
                        <option value="phase_change">Phase Change</option>
                        <option value="task_completion">Task Completion</option>
                    </select>
                    <ChevronDown size={16} className="select-chevron" />
                </div>
            </div>

            {formData.trigger_type === 'task_completion' && (
                <div className="form-group full">
                    <label className="subtext">Triggered By (Auto Task)</label>
                    <SearchSelect
                        value={formData.trigger_task_id}
                        onChange={(value) => setFormData({ ...formData, trigger_task_id: value })}
                        options={autoTasks.reduce((acc, task) => {
                            acc[task.id] = task.name;
                            return acc;
                        }, {})}
                        placeholder="Select auto task that triggers this one"
                    />
                </div>
            )}

            <div className="form-group">
                <label className="subtext">Create Task After (Days)</label>
                <input
                    type="number"
                    value={formData.create_delay_days}
                    onChange={(e) => setFormData({ ...formData, create_delay_days: parseInt(e.target.value) })}
                    min="0"
                />
            </div>

            <div className="form-group">
                <label className="subtext">Due In (Days)</label>
                <input
                    type="number"
                    value={formData.due_days}
                    onChange={(e) => setFormData({ ...formData, due_days: parseInt(e.target.value) })}
                    min="0"
                />
            </div>

            {formData.trigger_type === 'phase_change' && (
                <>
                    <div className="form-group full">
                        <label className="subtext">Repeat Every (Days) - Optional</label>
                        <input
                            type="number"
                            value={formData.repeat_interval_days || ''}
                            onChange={(e) => setFormData({ ...formData, repeat_interval_days: e.target.value ? parseInt(e.target.value) : null })}
                            min="1"
                            placeholder="Leave empty for no repeat"
                        />
                    </div>

                    {formData.repeat_interval_days && (
                        <Checkbox
                            value={formData.repeat_only_same_phase}
                            onChange={(checked) => setFormData({ ...formData, repeat_only_same_phase: checked })}
                            label="Only repeat if case is still in the same phase"
                        />
                    )}
                </>
            )}

            <Checkbox
                value={formData.only_if_in_phase}
                onChange={(checked) => setFormData({ ...formData, only_if_in_phase: checked })}
                label="Only create task if case is still in associated phase"
            />

            <div className="form-group full">
                <label className="subtext">Assign To Role</label>
                <SearchSelect
                    value={formData.assign_to_role_id}
                    onChange={(value) => setFormData({ ...formData, assign_to_role_id: value })}
                    options={roleOptions}
                    placeholder="Select role"
                />
            </div>

            <div className="form-group full">
                <label className="subtext">Also Notify Roles (Optional)</label>
                <SearchSelect
                    value={null}
                    onChange={(value) => {
                        if (!value) return;
                        const current = formData.notify_additional_roles || [];
                        if (current.includes(value)) {
                            setFormData({ ...formData, notify_additional_roles: current.filter(id => id !== value) });
                        } else {
                            setFormData({ ...formData, notify_additional_roles: [...current, value] });
                        }
                    }}
                    options={roleOptions}
                    placeholder="Select additional roles"
                />
                {formData.notify_additional_roles && formData.notify_additional_roles.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {formData.notify_additional_roles.map(roleId => (
                            <span key={roleId} style={{ 
                                padding: '4px 8px', 
                                backgroundColor: 'var(--border-color)', 
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}>
                                {roleOptions[roleId]}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="form-group full">
                <label className="subtext">Case Types (Optional)</label>
                <SearchSelect
                    value={null}
                    onChange={(value) => {
                        if (!value) return;
                        const current = formData.case_type_ids || [];
                        if (current.includes(value)) {
                            setFormData({ ...formData, case_type_ids: current.filter(id => id !== value) });
                        } else {
                            setFormData({ ...formData, case_type_ids: [...current, value] });
                        }
                    }}
                    options={caseTypeOptions}
                    placeholder="Select case types"
                />
                {formData.case_type_ids && formData.case_type_ids.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                        {formData.case_type_ids.map(caseTypeId => (
                            <span key={caseTypeId} style={{ 
                                padding: '4px 8px', 
                                backgroundColor: 'var(--border-color)', 
                                borderRadius: '4px',
                                fontSize: '12px'
                            }}>
                                {caseTypeOptions[caseTypeId]}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="form-group full">
                <label className="subtext">Task Subject</label>
                <input
                    type="text"
                    value={formData.task_subject}
                    onChange={(e) => setFormData({ ...formData, task_subject: e.target.value })}
                    placeholder="Subject for the created task"
                />
            </div>

            <div className="form-group full">
                <label className="subtext">Task Content</label>
                <textarea
                    value={formData.task_content}
                    onChange={(e) => setFormData({ ...formData, task_content: e.target.value })}
                    placeholder="Description for the created task"
                    rows={4}
                />
            </div>
        </div>
    </Modal>
);

export const Tasks = () => {
    const [phases, setPhases] = useState([]);
    const [selectedPhase, setSelectedPhase] = useState(null);
    const [autoTasks, setAutoTasks] = useState([]);
    const navVariant = useNavVariant();
    const [roles, setRoles] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        phase_id: null,
        trigger_type: 'phase_change',
        trigger_task_id: null,
        create_delay_days: 0,
        due_days: 7,
        repeat_interval_days: null,
        repeat_only_same_phase: true,
        only_if_in_phase: true,
        assign_to_role_id: null,
        notify_additional_roles: [],
        case_type_ids: [],
        task_subject: '',
        task_content: ''
    });

    useEffect(() => {
        document.title = "Task Workflows | CaseDB";

        fetchPhases();
        fetchRoles();
        fetchCaseTypes();
    }, []);

    useEffect(() => {
        if (selectedPhase) {
            fetchAutoTasksForPhase(selectedPhase.id);
        }
    }, [selectedPhase]);

    const fetchPhases = async () => {
        try {
            const response = await fetch('https://api.casedb.co/phases.php', { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setPhases(data.phases || []);
            }
        } catch (error) {
            console.error('Error fetching phases:', error);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await fetch('https://api.casedb.co/roles.php', { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setRoles(data.roles || []);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchCaseTypes = async () => {
        try {
            const response = await fetch('https://api.casedb.co/case_types.php', { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setCaseTypes(data.case_types || []);
            }
        } catch (error) {
            console.error('Error fetching case types:', error);
        }
    };

    const fetchAutoTasksForPhase = async (phaseId) => {
        try {
            setLoading(true);
            const response = await fetch(`https://api.casedb.co/auto_tasks.php?phase_id=${phaseId}`, { 
                credentials: 'include' 
            });
            const data = await response.json();
            if (data.success) {
                setAutoTasks(data.auto_tasks || []);
            }
        } catch (error) {
            console.error('Error fetching auto tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = (phase) => {
        setSelectedPhase(phase);
        setFormData({
            ...formData,
            phase_id: phase.id,
            name: '',
            task_subject: '',
            task_content: '',
            notify_additional_roles: [],
            case_type_ids: []
        });
        setEditingTask(null);
        setShowCreateModal(true);
    };

    const openEditModal = (autoTask) => {
        setEditingTask(autoTask);
        setFormData({
            id: autoTask.id,
            name: autoTask.name,
            phase_id: autoTask.phase_id,
            trigger_type: autoTask.trigger_type,
            trigger_task_id: autoTask.trigger_task_id,
            create_delay_days: autoTask.create_delay_days,
            due_days: autoTask.due_days,
            repeat_interval_days: autoTask.repeat_interval_days,
            repeat_only_same_phase: autoTask.repeat_only_same_phase == 1,
            only_if_in_phase: autoTask.only_if_in_phase == 1,
            assign_to_role_id: autoTask.assign_to_role_id,
            notify_additional_roles: (autoTask.notify_additional_roles || []).map(id => id?.toString()).filter(Boolean),
            case_type_ids: (autoTask.case_type_ids || []).map(id => id?.toString()).filter(Boolean),
            task_subject: autoTask.task_subject,
            task_content: autoTask.task_content
        });
        setShowCreateModal(true);
    };

    const saveAutoTask = async () => {
        try {
            setLoading(true);
            const method = editingTask ? 'PUT' : 'POST';
            const response = await fetch('https://api.casedb.co/auto_tasks.php', {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            
            const data = await response.json();
            if (data.success) {
                setShowCreateModal(false);
                if (selectedPhase) {
                    fetchAutoTasksForPhase(selectedPhase.id);
                }
            } else {
                alert(data.message || 'Failed to save auto task');
            }
        } catch (error) {
            console.error('Error saving auto task:', error);
            alert('Failed to save auto task');
        } finally {
            setLoading(false);
        }
    };

    const deleteAutoTask = async (id) => {
        if (!window.confirm('Are you sure you want to delete this auto task?')) return;
        
        try {
            const response = await fetch('https://api.casedb.co/auto_tasks.php', {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });
            
            const data = await response.json();
            if (data.success) {
                if (selectedPhase) {
                    fetchAutoTasksForPhase(selectedPhase.id);
                }
            }
        } catch (error) {
            console.error('Error deleting auto task:', error);
        }
    };

    const roleOptions = roles.reduce((acc, role) => {
        acc[role.id] = role.role_name || role.role || role.name;
        return acc;
    }, {});

    const caseTypeOptions = caseTypes.reduce((acc, type) => {
        acc[type.id] = type.name;
        return acc;
    }, {});

    return (
        <div className={`firm-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id='page-header' className='nm'>
                <h2>
                    Task Workflows
                </h2>
                <span className="subtext">Automate task creation based on phase changes and task completions</span>
            </div>

            <div className="content-wrapper">
                {!selectedPhase ? (
                    <div className="task-phase-container">
                        {phases.map(phase => (
                            <div
                                key={phase.id}
                                className="task-phase"
                                onClick={() => setSelectedPhase(phase)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="case-card-content">
                                    <h4>{phase.phase}</h4>
                                    <div className="case-card-subtitle">Click to manage auto-tasks</div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="phase-detail">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <div>
                                <h2>{selectedPhase.phase}</h2>
                                <p className="subtext">Manage automatic task creation for this phase</p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button className="action alt" onClick={() => setSelectedPhase(null)}>
                                    ← Back to Phases
                                </button>
                                <button className="action" onClick={() => openCreateModal(selectedPhase)}>
                                    <Plus size={16} /> Create Auto-Task
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading">Loading auto tasks...</div>
                        ) : autoTasks.length === 0 ? (
                            <div className="empty-state" style={{ 
                                textAlign: 'center', 
                                padding: '60px 20px',
                                backgroundColor: 'var(--secondary-color)',
                                borderRadius: '12px',
                                border: '1px solid var(--border-color)'
                            }}>
                                <Calendar size={48} style={{ color: 'var(--subtext-color)', marginBottom: '16px' }} />
                                <h3>No Auto-Tasks Yet</h3>
                                <p className="subtext" style={{ marginBottom: '20px' }}>
                                    Create your first auto-task to automate task creation for this phase
                                </p>
                                <button className="action" onClick={() => openCreateModal(selectedPhase)}>
                                    <Plus size={16} /> Create Auto-Task
                                </button>
                            </div>
                        ) : (
                            <div className="auto-tasks-list">
                                {autoTasks.map(task => (
                                    <div key={task.id} className="auto-task-card" style={{
                                        padding: '20px',
                                        backgroundColor: 'var(--secondary-color)',
                                        borderRadius: '12px',
                                        border: '1px solid var(--border-color)',
                                        marginBottom: '16px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                            <div style={{ flex: 1 }}>
                                                <h3 style={{ margin: '0 0 8px 0' }}>{task.name}</h3>
                                                <div className="subtext" style={{ fontSize: '14px', marginBottom: '12px' }}>
                                                    {task.task_subject}
                                                </div>
                                                
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Play size={14} />
                                                        <span>{task.trigger_type === 'phase_change' ? 'Phase Change' : 'Task Completion'}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <Calendar size={14} />
                                                        <span>Create after {task.create_delay_days} days</span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <AlertCircle size={14} />
                                                        <span>Due in {task.due_days} days</span>
                                                    </div>
                                                    {task.repeat_interval_days && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <Repeat size={14} />
                                                            <span>Repeat every {task.repeat_interval_days} days</span>
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <User size={14} />
                                                        <span>{task.assign_to_role_name}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button 
                                                    className="action alt"
                                                    onClick={() => openEditModal(task)}
                                                    style={{ padding: '8px 12px' }}
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button 
                                                    className="action alt"
                                                    onClick={() => deleteAutoTask(task.id)}
                                                    style={{ padding: '8px 12px', color: '#ef4444' }}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showCreateModal && (
                <AutoTaskModal
                    editingTask={editingTask}
                    loading={loading}
                    formData={formData}
                    setFormData={setFormData}
                    autoTasks={autoTasks}
                    roleOptions={roleOptions}
                    caseTypeOptions={caseTypeOptions}
                    saveAutoTask={saveAutoTask}
                    onClose={() => setShowCreateModal(false)}
                />
            )}
        </div>
    );
};